// ============================================================================
// Electrical Schematics Course Page
// Comprehensive, Interactive Course on Industrial Electrical Schematics
// IEEE / ANSI / NEMA Standards & Practical Maintenance Troubleshooting
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  BookOpen,
  Zap,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  Layers,
  Activity,
  Sliders,
  Compass,
  Award,
  Search,
  RotateCcw,
  Check,
  ArrowRight,
  ArrowLeft,
  Info,
  AlertTriangle,
  Play,
  Filter,
  Flame,
  HelpCircle,
  Clock,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import {
  COURSE_MODULES,
  SCHEMATIC_SYMBOLS,
  FINAL_EXAM_QUESTIONS,
  CourseLesson,
  SchematicSymbolItem,
} from '../data/schematicsCourseData';

export interface SchematicsCoursePageProps {
  onNavigate: (path: string) => void;
  onStartExam?: (track: string, mode: string, categoryId?: string) => void;
}

const STORAGE_KEY = 'schematics_course_progress_v1';

export function SchematicsCoursePage({ onNavigate, onStartExam }: SchematicsCoursePageProps) {
  // Navigation & Tab State
  const [activeTab, setActiveTab] = useState<'lessons' | 'tools' | 'symbols' | 'exam'>('lessons');
  const [activeModuleId, setActiveModuleId] = useState<string>(COURSE_MODULES[0].id);
  const [activeLessonId, setActiveLessonId] = useState<string>(COURSE_MODULES[0].lessons[0].id);
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({
    'mod-1': true,
    'mod-2': true,
  });

  // Progress state stored in localStorage
  const [completedLessons, setCompletedLessons] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Lesson Quiz State
  const [quizAnswers, setQuizAnswers] = useState<Record<string, number>>({});
  const [quizSubmitted, setQuizSubmitted] = useState<Record<string, boolean>>({});

  // Active Tool Sub-tab
  const [activeTool, setActiveTool] = useState<'colorcode' | 'logic' | 'regulation' | 'filter' | 'testpoints'>('colorcode');

  // Tool 1: Resistor Color Code State
  const [bandCount, setBandCount] = useState<4 | 5>(4);
  const [band1, setBand1] = useState<number>(4); // Yellow (4)
  const [band2, setBand2] = useState<number>(7); // Violet (7)
  const [band3Digit, setBand3Digit] = useState<number>(0); // For 5-band
  const [multiplier, setMultiplier] = useState<number>(2); // Red (x100)
  const [tolerance, setTolerance] = useState<number>(5); // Gold (5%)
  const [failureRate, setFailureRate] = useState<number>(1); // Brown (1%)

  // Tool 2: Logic Simulator State
  const [selectedGate, setSelectedGate] = useState<'NOT' | 'AND' | 'OR' | 'NAND' | 'NOR' | 'XOR' | 'COMPOUND'>('AND');
  const [logicInputX, setLogicInputX] = useState<0 | 1>(1);
  const [logicInputY, setLogicInputY] = useState<0 | 1>(0);

  // Tool 3: Voltage Divider vs Diode Reducer State
  const [loadResistance, setLoadResistance] = useState<number>(672);

  // Tool 4: Op-Amp Filter Calculator State
  const [filterType, setFilterType] = useState<'lowpass' | 'highpass' | 'peak' | 'notch'>('lowpass');
  const [filterResistorKOhms, setFilterResistorKOhms] = useState<number>(10);
  const [filterCapacitorMicroF, setFilterCapacitorMicroF] = useState<number>(0.0159);

  // Tool 5: Multimeter Test Points Simulator State
  const [amplifierFault, setAmplifierFault] = useState<'normal' | 'open_r1' | 'open_r4' | 'short_c1'>('normal');
  const [selectedTP, setSelectedTP] = useState<'TP1' | 'TP2' | 'TP3' | 'VCC' | 'GND'>('TP3');

  // Symbol Dictionary State
  const [symbolSearch, setSymbolSearch] = useState<string>('');
  const [symbolCategory, setSymbolCategory] = useState<string>('All');

  // Final Exam State
  const [examActive, setExamActive] = useState<boolean>(false);
  const [examAnswers, setExamAnswers] = useState<Record<string, number>>({});
  const [examSubmitted, setExamSubmitted] = useState<boolean>(false);
  const [examScore, setExamScore] = useState<number>(0);
  const [candidateName, setCandidateName] = useState<string>('Maintenance Technician');

  // Find Current Lesson
  const currentModule = useMemo(() => {
    return COURSE_MODULES.find((m) => m.id === activeModuleId) || COURSE_MODULES[0];
  }, [activeModuleId]);

  const currentLesson = useMemo(() => {
    for (const m of COURSE_MODULES) {
      const found = m.lessons.find((l) => l.id === activeLessonId);
      if (found) return found;
    }
    return COURSE_MODULES[0].lessons[0];
  }, [activeLessonId]);

  // Total lessons count
  const allLessons = useMemo(() => {
    return COURSE_MODULES.flatMap((m) => m.lessons);
  }, []);

  const progressPercent = Math.round((completedLessons.length / allLessons.length) * 100);

  // Persist progress
  const toggleLessonComplete = (lessonId: string) => {
    setCompletedLessons((prev) => {
      const updated = prev.includes(lessonId)
        ? prev.filter((id) => id !== lessonId)
        : [...prev, lessonId];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (err) {
        console.warn('Failed to save course progress', err);
      }
      return updated;
    });
  };

  const handleNextLesson = () => {
    const currentIndex = allLessons.findIndex((l) => l.id === activeLessonId);
    if (currentIndex < allLessons.length - 1) {
      const nextLesson = allLessons[currentIndex + 1];
      setActiveLessonId(nextLesson.id);
      // Also update module if needed
      for (const m of COURSE_MODULES) {
        if (m.lessons.some((l) => l.id === nextLesson.id)) {
          setActiveModuleId(m.id);
          setExpandedModules((prev) => ({ ...prev, [m.id]: true }));
          break;
        }
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevLesson = () => {
    const currentIndex = allLessons.findIndex((l) => l.id === activeLessonId);
    if (currentIndex > 0) {
      const prevLesson = allLessons[currentIndex - 1];
      setActiveLessonId(prevLesson.id);
      for (const m of COURSE_MODULES) {
        if (m.lessons.some((l) => l.id === prevLesson.id)) {
          setActiveModuleId(m.id);
          setExpandedModules((prev) => ({ ...prev, [m.id]: true }));
          break;
        }
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Color code calculation
  const colorCodeResult = useMemo(() => {
    let baseDigits = 0;
    if (bandCount === 4) {
      baseDigits = band1 * 10 + band2;
    } else {
      baseDigits = band1 * 100 + band2 * 10 + band3Digit;
    }
    const multValue = Math.pow(10, multiplier);
    const nominalOhms = baseDigits * multValue;

    let displayStr = '';
    if (nominalOhms >= 1_000_000) {
      displayStr = `${(nominalOhms / 1_000_000).toFixed(2)} MΩ`;
    } else if (nominalOhms >= 1_000) {
      displayStr = `${(nominalOhms / 1_000).toFixed(2)} kΩ`;
    } else {
      displayStr = `${nominalOhms.toFixed(1)} Ω`;
    }

    const tolFraction = tolerance / 100;
    const minOhms = nominalOhms * (1 - tolFraction);
    const maxOhms = nominalOhms * (1 + tolFraction);

    return {
      nominalOhms,
      displayStr,
      minOhms: minOhms >= 1000 ? `${(minOhms / 1000).toFixed(2)} kΩ` : `${minOhms.toFixed(1)} Ω`,
      maxOhms: maxOhms >= 1000 ? `${(maxOhms / 1000).toFixed(2)} kΩ` : `${maxOhms.toFixed(1)} Ω`,
    };
  }, [bandCount, band1, band2, band3Digit, multiplier, tolerance]);

  // Logic Simulator Calculation
  const logicOutput = useMemo(() => {
    const x = logicInputX;
    const y = logicInputY;
    switch (selectedGate) {
      case 'NOT':
        return (x === 1 ? 0 : 1) as 0 | 1;
      case 'AND':
        return (x === 1 && y === 1 ? 1 : 0) as 0 | 1;
      case 'OR':
        return (x === 1 || y === 1 ? 1 : 0) as 0 | 1;
      case 'NAND':
        return (x === 1 && y === 1 ? 0 : 1) as 0 | 1;
      case 'NOR':
        return (x === 0 && y === 0 ? 1 : 0) as 0 | 1;
      case 'XOR':
        return (x !== y ? 1 : 0) as 0 | 1;
      case 'COMPOUND':
        // Case 1: (NOT X) AND (NOT Y) == NOR
        const notX = x === 1 ? 0 : 1;
        const notY = y === 1 ? 0 : 1;
        return (notX === 1 && notY === 1 ? 1 : 0) as 0 | 1;
      default:
        return 0;
    }
  }, [selectedGate, logicInputX, logicInputY]);

  // Voltage Divider vs Diode Reducer calculation
  const regulationData = useMemo(() => {
    // Battery 6.30V
    // Divider: R1=220, R2=328, R3=465, R4=671. Tap E2 is across R1+R2 (548 ohms).
    // R_bottom = 548 || RL. R_top = 465 + 671 = 1136 ohms.
    // E2 = 6.30 * (R_bottom / (R_top + R_bottom))
    const rBottomUnloaded = 548;
    const rTop = 1136;
    const rBottomLoaded = (rBottomUnloaded * loadResistance) / (rBottomUnloaded + loadResistance);
    const vDivider = 6.30 * (rBottomLoaded / (rTop + rBottomLoaded));

    // Diode Reducer: Two diodes in series forward drop (~1.4V) with battery 6.30V
    // Dropping very slowly from 5.08V at 3250 down to 4.79V at 220
    const vDiode = 5.15 - 0.25 * Math.log10(3300 / Math.max(loadResistance, 100));

    return {
      vDivider: vDivider.toFixed(2),
      vDiode: Math.max(4.75, Math.min(5.12, vDiode)).toFixed(2),
    };
  }, [loadResistance]);

  // Op-Amp Active Filter Cutoff Calculation
  const filterCutoffHz = useMemo(() => {
    const rOhms = filterResistorKOhms * 1000;
    const cFarads = filterCapacitorMicroF * 1e-6;
    if (rOhms <= 0 || cFarads <= 0) return 0;
    const f = 1 / (2 * Math.PI * rOhms * cFarads);
    return Math.round(f);
  }, [filterResistorKOhms, filterCapacitorMicroF]);

  // Multimeter Test Points Simulator calculation
  const tpReading = useMemo(() => {
    switch (amplifierFault) {
      case 'normal':
        if (selectedTP === 'TP1') return { val: '1.85 V', status: 'normal', desc: 'Emitter voltage drop across R1 (normal DC bias).' };
        if (selectedTP === 'TP2') return { val: '2.52 V', status: 'normal', desc: 'Base voltage (~0.67V higher than Emitter, forward biased).' };
        if (selectedTP === 'TP3') return { val: '6.45 V', status: 'normal', desc: 'Collector voltage (centered near Vcc/2, maximum swing).' };
        if (selectedTP === 'VCC') return { val: '12.00 V', status: 'normal', desc: 'Full regulated DC supply rail.' };
        return { val: '0.00 V', status: 'normal', desc: 'Chassis electrical ground reference.' };

      case 'open_r1':
        if (selectedTP === 'TP1') return { val: '0.00 V', status: 'fault', desc: 'FAULT: Emitter resistor R1 open. No current path to ground; emitter floating near 0V.' };
        if (selectedTP === 'TP2') return { val: '1.18 V', status: 'fault', desc: 'FAULT: Base voltage pulled low because transistor is cut off.' };
        if (selectedTP === 'TP3') return { val: '12.00 V', status: 'fault', desc: 'FAULT: Collector reads full +12V! Zero current through R4, transistor is completely OFF.' };
        if (selectedTP === 'VCC') return { val: '12.00 V', status: 'normal', desc: 'Supply rail is normal.' };
        return { val: '0.00 V', status: 'normal', desc: 'Chassis ground.' };

      case 'open_r4':
        if (selectedTP === 'TP1') return { val: '0.00 V', status: 'fault', desc: 'FAULT: No collector current flowing down through emitter.' };
        if (selectedTP === 'TP2') return { val: '1.20 V', status: 'fault', desc: 'FAULT: Base voltage altered due to lack of collector feedback.' };
        if (selectedTP === 'TP3') return { val: '0.00 V', status: 'fault', desc: 'FAULT: Collector resistor R4 is open! Zero voltage reaches collector terminal.' };
        if (selectedTP === 'VCC') return { val: '12.00 V', status: 'normal', desc: 'Supply rail is normal.' };
        return { val: '0.00 V', status: 'normal', desc: 'Chassis ground.' };

      case 'short_c1':
        if (selectedTP === 'TP1') return { val: '0.00 V', status: 'fault', desc: 'FAULT: Emitter bypass capacitor C1 is shorted directly to chassis ground!' };
        if (selectedTP === 'TP2') return { val: '0.72 V', status: 'fault', desc: 'FAULT: Base clamped at diode drop above grounded emitter; transistor heavily saturated.' };
        if (selectedTP === 'TP3') return { val: '0.35 V', status: 'fault', desc: 'FAULT: Transistor driven hard into saturation; collector voltage collapsed near 0V.' };
        if (selectedTP === 'VCC') return { val: '12.00 V', status: 'normal', desc: 'Supply rail is normal.' };
        return { val: '0.00 V', status: 'normal', desc: 'Chassis ground.' };
    }
  }, [amplifierFault, selectedTP]);

  // Filtered symbols
  const filteredSymbols = useMemo(() => {
    return SCHEMATIC_SYMBOLS.filter((s) => {
      const matchesSearch =
        s.name.toLowerCase().includes(symbolSearch.toLowerCase()) ||
        s.letterCode.toLowerCase().includes(symbolSearch.toLowerCase()) ||
        s.description.toLowerCase().includes(symbolSearch.toLowerCase());
      const matchesCat = symbolCategory === 'All' || s.category === symbolCategory;
      return matchesSearch && matchesCat;
    });
  }, [symbolSearch, symbolCategory]);

  // Final Exam submission
  const handleFinalExamSubmit = () => {
    let score = 0;
    FINAL_EXAM_QUESTIONS.forEach((q) => {
      if (examAnswers[q.id] === q.correctIndex) {
        score++;
      }
    });
    setExamScore(score);
    setExamSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-20 transition-colors">
      {/* Top Banner & Course Title */}
      <div className="bg-gradient-to-r from-sky-50 via-slate-50 to-amber-50/60 dark:from-sky-950 dark:via-slate-900 dark:to-amber-950 border-b border-border pt-8 pb-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="warning" className="px-2.5 py-1 text-xs font-bold gap-1.5 shadow-sm">
                <Zap className="h-3.5 w-3.5 fill-current" />
                Comprehensive Technical Course
              </Badge>
              <Badge variant="secondary" className="text-xs">
                Industrial Electrical Standards Curriculum
              </Badge>
            </div>

            {/* Overall Progress Widget */}
            <div className="flex items-center gap-3 bg-card px-4 py-2 rounded-xl border border-border shadow-sm">
              <div className="text-right">
                <div className="text-xs font-semibold text-muted-foreground">Course Completion</div>
                <div className="text-sm font-bold text-amber-600 dark:text-amber-400 font-mono">
                  {completedLessons.length} / {allLessons.length} Lessons ({progressPercent}%)
                </div>
              </div>
              <div className="h-9 w-9 rounded-full bg-amber-500/15 border-2 border-amber-500 flex items-center justify-center font-bold text-amber-700 dark:text-amber-300 text-xs font-mono">
                {progressPercent}%
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-sky-600 dark:text-sky-400" />
              Beginner to Advanced Electrical Schematics Course
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground max-w-4xl leading-relaxed">
              Master the visual language of electrical circuits from the ground up: schematic symbology, block diagrams, signal tracing across multi-page blueprints, passive and active components, ladder circuits, and hands-on component-level troubleshooting.
            </p>
          </div>

          {/* Navigation Tabs Bar */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
            <button
              onClick={() => setActiveTab('lessons')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === 'lessons'
                  ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20'
                  : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
              }`}
            >
              <BookOpen className="h-4 w-4" />
              Course Lessons ({allLessons.length})
            </button>

            <button
              onClick={() => setActiveTab('tools')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === 'tools'
                  ? 'bg-sky-500 text-slate-950 shadow-md shadow-sky-500/20'
                  : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
              }`}
            >
              <Sliders className="h-4 w-4" />
              Interactive Diagnostic Tools (5)
            </button>

            <button
              onClick={() => setActiveTab('symbols')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === 'symbols'
                  ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20'
                  : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
              }`}
            >
              <Search className="h-4 w-4" />
              Symbol Dictionary (Appendix A)
            </button>

            <button
              onClick={() => setActiveTab('exam')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === 'exam'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20'
                  : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
              }`}
            >
              <Award className="h-4 w-4" />
              Certification Final Exam (30 Qs)
            </button>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* ================================================================== */}
        {/* TAB 1: LESSONS & CURRICULUM */}
        {/* ================================================================== */}
        {activeTab === 'lessons' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Sidebar: Module Accordion */}
            <div className="lg:col-span-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                  Course Modules &amp; Chapters
                </h3>
                <span className="text-xs text-muted-foreground font-mono">
                  {completedLessons.length}/{allLessons.length} Complete
                </span>
              </div>

              <div className="space-y-3">
                {COURSE_MODULES.map((module) => {
                  const isExpanded = !!expandedModules[module.id];
                  const moduleLessons = module.lessons;
                  const completedInModule = moduleLessons.filter((l) => completedLessons.includes(l.id)).length;
                  const isModuleActive = module.id === activeModuleId;

                  return (
                    <div
                      key={module.id}
                      className={`rounded-xl border transition-all ${
                        isModuleActive
                          ? 'border-sky-500/50 bg-sky-50/50 dark:bg-slate-900/90 shadow-sm'
                          : 'border-border bg-card'
                      }`}
                    >
                      <button
                        onClick={() =>
                          setExpandedModules((prev) => ({
                            ...prev,
                            [module.id]: !prev[module.id],
                          }))
                        }
                        className="w-full flex items-center justify-between p-3.5 text-left"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="h-6 w-6 rounded-md bg-sky-500/20 text-sky-600 dark:text-sky-400 font-bold text-xs flex items-center justify-center font-mono">
                            {module.number}
                          </span>
                          <div>
                            <div className="text-xs font-bold text-foreground">{module.title}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {completedInModule}/{moduleLessons.length} lessons done
                            </div>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="p-2 pt-0 space-y-1 border-t border-border/40">
                          {moduleLessons.map((lesson) => {
                            const isCurrent = lesson.id === activeLessonId;
                            const isDone = completedLessons.includes(lesson.id);

                            return (
                              <button
                                key={lesson.id}
                                onClick={() => {
                                  setActiveModuleId(module.id);
                                  setActiveLessonId(lesson.id);
                                  window.scrollTo({ top: 220, behavior: 'smooth' });
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium text-left transition-all ${
                                  isCurrent
                                    ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300 font-bold border border-sky-500/40'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                                }`}
                              >
                                <div className="flex items-center gap-2 truncate">
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleLessonComplete(lesson.id);
                                    }}
                                    className={`h-4 w-4 rounded flex items-center justify-center shrink-0 transition-colors ${
                                      isDone
                                        ? 'bg-emerald-500 text-slate-950'
                                        : 'border border-slate-300 dark:border-slate-600 hover:border-slate-500'
                                    }`}
                                  >
                                    {isDone && <Check className="h-3 w-3 stroke-[3]" />}
                                  </div>
                                  <span className="truncate">{lesson.title}</span>
                                </div>
                                <span className="text-[10px] text-muted-foreground shrink-0 font-mono ml-2">
                                  {lesson.durationMinutes}m
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Quick Jump to Diagnostic Tools */}
              <div className="p-4 rounded-xl border border-sky-500/30 bg-sky-50/60 dark:bg-sky-950/20 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-sky-700 dark:text-sky-400">
                  <Sliders className="h-4 w-4" />
                  Hands-On Workbenches
                </div>
                <p className="text-xs text-muted-foreground">
                  Experiment with real circuit behaviors: calculate resistor color codes, simulate logic gates, test load sag, and isolate amplifier faults with virtual multimeter probes.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setActiveTab('tools')}
                  className="w-full text-xs font-bold mt-1"
                >
                  Open Interactive Workbenches
                </Button>
              </div>
            </div>

            {/* Main Lesson Content Area */}
            <div className="lg:col-span-8 space-y-6">
              <Card className="border-border bg-card shadow-lg">
                <CardHeader className="border-b border-border/60 pb-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Badge variant="secondary" className="font-mono text-xs">
                      Module {currentModule.number} • Lesson {currentLesson.id}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {currentLesson.durationMinutes} min read
                      </span>
                      <Button
                        variant={completedLessons.includes(currentLesson.id) ? 'secondary' : 'amber'}
                        size="sm"
                        onClick={() => toggleLessonComplete(currentLesson.id)}
                        className="text-xs font-bold gap-1.5"
                      >
                        {completedLessons.includes(currentLesson.id) ? (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                            Completed
                          </>
                        ) : (
                          'Mark Lesson Done'
                        )}
                      </Button>
                    </div>
                  </div>
                  <CardTitle className="text-2xl font-bold text-foreground pt-2">
                    {currentLesson.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    {currentLesson.summary}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-6 space-y-8">
                  {/* Schematic Diagram Asset if present */}
                  {currentLesson.diagramUrl && (
                    <div className="space-y-2">
                      <div className="rounded-xl overflow-hidden border border-border bg-slate-950 p-2 shadow-inner">
                        <img
                          src={currentLesson.diagramUrl}
                          alt={currentLesson.title}
                          className="w-full h-auto max-h-[460px] object-contain mx-auto"
                        />
                      </div>
                      {currentLesson.diagramCaption && (
                        <p className="text-xs text-center text-muted-foreground italic">
                          {currentLesson.diagramCaption}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Lesson Text Sections */}
                  {currentLesson.sections.map((section, idx) => (
                    <div key={idx} className="space-y-3">
                      <h3 className="text-lg font-bold text-sky-600 dark:text-sky-400 border-l-4 border-sky-500 pl-3">
                        {section.heading}
                      </h3>
                      <div className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line space-y-2">
                        {section.content}
                      </div>

                      {/* Callout box if present */}
                      {section.callout && (
                        <div
                          className={`p-4 rounded-xl border space-y-1 text-xs ${
                            section.callout.type === 'tip'
                              ? 'bg-sky-50 border-sky-200 text-sky-950 dark:bg-sky-950/40 dark:border-sky-500/40 dark:text-sky-200'
                              : section.callout.type === 'warning'
                              ? 'bg-rose-50 border-rose-200 text-rose-950 dark:bg-rose-950/40 dark:border-rose-500/40 dark:text-rose-200'
                              : section.callout.type === 'remember'
                              ? 'bg-amber-50 border-amber-200 text-amber-950 dark:bg-amber-950/40 dark:border-amber-500/40 dark:text-amber-200'
                              : section.callout.type === 'weird'
                              ? 'bg-purple-50 border-purple-200 text-purple-950 dark:bg-purple-950/40 dark:border-purple-500/40 dark:text-purple-200'
                              : 'bg-emerald-50 border-emerald-200 text-emerald-950 dark:bg-emerald-950/40 dark:border-emerald-500/40 dark:text-emerald-200'
                          }`}
                        >
                          <div className="font-bold flex items-center gap-1.5 uppercase tracking-wide text-[11px]">
                            {section.callout.type === 'warning' ? (
                              <AlertTriangle className="h-4 w-4" />
                            ) : section.callout.type === 'tip' ? (
                              <Sparkles className="h-4 w-4" />
                            ) : (
                              <Info className="h-4 w-4" />
                            )}
                            {section.callout.title}
                          </div>
                          <p className="leading-relaxed">{section.callout.text}</p>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Lesson Knowledge Check Quiz */}
                  {currentLesson.quiz && currentLesson.quiz.length > 0 && (
                    <div className="pt-6 border-t border-border/80 space-y-6">
                      <div className="flex items-center gap-2">
                        <HelpCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                        <h4 className="text-base font-bold text-foreground">Lesson Knowledge Check</h4>
                      </div>

                      {currentLesson.quiz.map((q) => {
                        const selectedIdx = quizAnswers[q.id];
                        const isRevealed = !!quizSubmitted[q.id];
                        const isCorrect = selectedIdx === q.correctIndex;

                        return (
                          <div key={q.id} className="p-4 rounded-xl border border-border bg-muted/30 dark:bg-slate-950/60 space-y-3">
                            <p className="text-sm font-semibold text-foreground">{q.prompt}</p>

                            <div className="space-y-2">
                              {q.options.map((opt, oIdx) => {
                                let btnClasses = 'border-border bg-card hover:bg-muted text-foreground';
                                if (isRevealed) {
                                  if (oIdx === q.correctIndex) {
                                    btnClasses = 'border-emerald-500 bg-emerald-500/15 text-emerald-900 dark:text-emerald-300 font-bold';
                                  } else if (selectedIdx === oIdx) {
                                    btnClasses = 'border-rose-500 bg-rose-500/15 text-rose-900 dark:text-rose-300';
                                  }
                                } else if (selectedIdx === oIdx) {
                                  btnClasses = 'border-amber-500 bg-amber-500/20 text-amber-900 dark:text-amber-300 font-bold';
                                }

                                return (
                                  <button
                                    key={oIdx}
                                    disabled={isRevealed}
                                    onClick={() => setQuizAnswers((prev) => ({ ...prev, [q.id]: oIdx }))}
                                    className={`w-full text-left p-3 rounded-lg border text-xs leading-relaxed transition-all ${btnClasses}`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <span className="font-mono font-bold shrink-0">{String.fromCharCode(65 + oIdx)}.</span>
                                      <span>{opt}</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>

                            {!isRevealed ? (
                              <Button
                                variant="amber"
                                size="sm"
                                disabled={selectedIdx === undefined}
                                onClick={() => setQuizSubmitted((prev) => ({ ...prev, [q.id]: true }))}
                                className="text-xs font-bold"
                              >
                                Submit Answer
                              </Button>
                            ) : (
                              <div
                                className={`p-3 rounded-lg border text-xs space-y-1 ${
                                  isCorrect ? 'bg-emerald-50 border-emerald-200 text-emerald-950 dark:bg-emerald-950/40 dark:border-emerald-500/40 dark:text-emerald-200' : 'bg-rose-50 border-rose-200 text-rose-950 dark:bg-rose-950/40 dark:border-rose-500/40 dark:text-rose-200'
                                }`}
                              >
                                <div className="font-bold flex items-center gap-1.5">
                                  {isCorrect ? <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />}
                                  {isCorrect ? 'Correct!' : 'Incorrect — Review Rationale:'}
                                </div>
                                <p className="leading-relaxed text-foreground/90 dark:text-slate-300">{q.explanation}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Bottom Navigation Buttons */}
                  <div className="pt-6 border-t border-border flex items-center justify-between gap-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handlePrevLesson}
                      className="gap-1.5 text-xs font-semibold"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Previous Lesson
                    </Button>

                    <Button
                      variant="amber"
                      size="sm"
                      onClick={handleNextLesson}
                      className="gap-1.5 text-xs font-bold"
                    >
                      Next Lesson
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ================================================================== */}
        {/* TAB 2: INTERACTIVE DIAGNOSTIC WORKBENCHES */}
        {/* ================================================================== */}
        {activeTab === 'tools' && (
          <div className="space-y-8">
            {/* Tool Selection Tabs */}
            <div className="flex flex-wrap items-center gap-2 border-b border-border pb-4">
              <button
                onClick={() => setActiveTool('colorcode')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeTool === 'colorcode'
                    ? 'bg-amber-500 text-slate-950 font-bold'
                    : 'bg-card text-muted-foreground hover:text-foreground border border-border'
                }`}
              >
                1. Resistor Color Decoder
              </button>
              <button
                onClick={() => setActiveTool('logic')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeTool === 'logic'
                    ? 'bg-amber-500 text-slate-950 font-bold'
                    : 'bg-card text-muted-foreground hover:text-foreground border border-border'
                }`}
              >
                2. Logic Gate Truth Table
              </button>
              <button
                onClick={() => setActiveTool('regulation')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeTool === 'regulation'
                    ? 'bg-amber-500 text-slate-950 font-bold'
                    : 'bg-card text-muted-foreground hover:text-foreground border border-border'
                }`}
              >
                3. Voltage Divider vs Diode Sag
              </button>
              <button
                onClick={() => setActiveTool('filter')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeTool === 'filter'
                    ? 'bg-amber-500 text-slate-950 font-bold'
                    : 'bg-card text-muted-foreground hover:text-foreground border border-border'
                }`}
              >
                4. Op-Amp Filter Calculator
              </button>
              <button
                onClick={() => setActiveTool('testpoints')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeTool === 'testpoints'
                    ? 'bg-amber-500 text-slate-950 font-bold'
                    : 'bg-card text-muted-foreground hover:text-foreground border border-border'
                }`}
              >
                5. Multimeter TP Diagnostic Bench
              </button>
            </div>

            {/* TOOL 1: Resistor Color Code Decoder */}
            {activeTool === 'colorcode' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="border-amber-500/30 bg-card shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <Badge variant="warning">Interactive Decoder</Badge>
                      <div className="flex items-center gap-1 bg-muted p-1 rounded-lg">
                        <button
                          onClick={() => setBandCount(4)}
                          className={`px-2 py-0.5 rounded text-xs font-bold transition-colors ${
                            bandCount === 4 ? 'bg-amber-500 text-slate-950' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          4-Band
                        </button>
                        <button
                          onClick={() => setBandCount(5)}
                          className={`px-2 py-0.5 rounded text-xs font-bold transition-colors ${
                            bandCount === 5 ? 'bg-amber-500 text-slate-950' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          5-Band
                        </button>
                      </div>
                    </div>
                    <CardTitle className="text-xl">Resistor Color Code Calculator &amp; Band Painter</CardTitle>
                    <CardDescription>
                      Click the color swatches to dynamically paint the resistor and calculate nominal ohms, tolerance bounds, and reliability drift.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* SVG Resistor Visualization */}
                    <div className="bg-slate-100 dark:bg-slate-950 rounded-xl p-4 border border-border flex items-center justify-center">
                      <svg width="100%" height="110" viewBox="0 0 460 110">
                        {/* Wire Leads */}
                        <line x1="20" y1="55" x2="90" y2="55" stroke="#94a3b8" strokeWidth="6" strokeLinecap="round" />
                        <line x1="370" y1="55" x2="440" y2="55" stroke="#94a3b8" strokeWidth="6" strokeLinecap="round" />
                        {/* Body */}
                        <rect x="90" y="20" width="280" height="70" rx="35" fill="#d97706" stroke="#b45309" strokeWidth="2" />
                        {/* Band 1 */}
                        <rect x="130" y="20" width="16" height="70" fill={getColorHex(band1)} />
                        {/* Band 2 */}
                        <rect x="175" y="20" width="16" height="70" fill={getColorHex(band2)} />
                        {/* Band 3 */}
                        {bandCount === 5 && (
                          <rect x="220" y="20" width="16" height="70" fill={getColorHex(band3Digit)} />
                        )}
                        {/* Multiplier Band */}
                        <rect x={bandCount === 5 ? 265 : 225} y="20" width="16" height="70" fill={getColorHex(multiplier)} />
                        {/* Tolerance Band */}
                        <rect x={bandCount === 5 ? 315 : 295} y="20" width="16" height="70" fill={getToleranceHex(tolerance)} />
                        {/* Reliability 5th band for 5-band */}
                        {bandCount === 5 && (
                          <rect x="345" y="20" width="12" height="70" fill={getFailureRateHex(failureRate)} />
                        )}
                      </svg>
                    </div>

                    {/* Calculated Output Display */}
                    <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-center space-y-1">
                      <span className="text-xs font-semibold text-muted-foreground">Calculated Resistance</span>
                      <div className="text-3xl font-black text-amber-600 dark:text-amber-400 font-mono">
                        {colorCodeResult.displayStr}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Tolerance: <span className="font-bold text-amber-700 dark:text-amber-300">±{tolerance}%</span> (Acceptable Range: {colorCodeResult.minOhms} to {colorCodeResult.maxOhms})
                      </div>
                      {bandCount === 5 && (
                        <div className="text-[11px] text-sky-600 dark:text-sky-400">
                          MIL-Spec Failure Rate: ±{failureRate}% max drift per 1,000 operating hours
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Color Swatch Selectors */}
                <Card className="border-border bg-card shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Color Band Selector Controls</CardTitle>
                    <CardDescription>Select digits, multipliers, and tolerances per Appendix B</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Band 1 */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Band 1 (First Significant Digit):</label>
                      <div className="flex flex-wrap gap-1">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((val) => (
                          <button
                            key={val}
                            onClick={() => setBand1(val)}
                            className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                              band1 === val ? 'ring-2 ring-foreground scale-105' : 'opacity-85 hover:opacity-100'
                            }`}
                            style={{ backgroundColor: getColorHex(val), color: val === 1 || val === 2 || val === 4 ? '#000' : '#fff' }}
                          >
                            {val}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Band 2 */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Band 2 (Second Significant Digit):</label>
                      <div className="flex flex-wrap gap-1">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((val) => (
                          <button
                            key={val}
                            onClick={() => setBand2(val)}
                            className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                              band2 === val ? 'ring-2 ring-foreground scale-105' : 'opacity-85 hover:opacity-100'
                            }`}
                            style={{ backgroundColor: getColorHex(val), color: val === 1 || val === 2 || val === 4 ? '#000' : '#fff' }}
                          >
                            {val}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Band 3 Digit (if 5-band) */}
                    {bandCount === 5 && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-foreground">Band 3 (Third Significant Digit):</label>
                        <div className="flex flex-wrap gap-1">
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((val) => (
                            <button
                              key={val}
                              onClick={() => setBand3Digit(val)}
                              className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                                band3Digit === val ? 'ring-2 ring-foreground scale-105' : 'opacity-85 hover:opacity-100'
                              }`}
                              style={{ backgroundColor: getColorHex(val), color: val === 1 || val === 2 || val === 4 ? '#000' : '#fff' }}
                            >
                              {val}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Multiplier */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Multiplier (Power of 10):</label>
                      <div className="flex flex-wrap gap-1">
                        {[
                          { val: 0, label: 'x1' },
                          { val: 1, label: 'x10' },
                          { val: 2, label: 'x100' },
                          { val: 3, label: 'x1k' },
                          { val: 4, label: 'x10k' },
                          { val: 5, label: 'x100k' },
                          { val: 6, label: 'x1M' },
                        ].map((item) => (
                          <button
                            key={item.val}
                            onClick={() => setMultiplier(item.val)}
                            className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                              multiplier === item.val ? 'ring-2 ring-foreground scale-105' : 'opacity-85 hover:opacity-100'
                            }`}
                            style={{ backgroundColor: getColorHex(item.val), color: item.val === 1 || item.val === 2 || item.val === 4 ? '#000' : '#fff' }}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Tolerance */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Tolerance Band:</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setTolerance(5)}
                          className={`px-3 py-1.5 rounded text-xs font-bold border ${
                            tolerance === 5 ? 'bg-yellow-600 text-white border-yellow-500' : 'bg-muted text-foreground border-border'
                          }`}
                        >
                          Gold (±5%)
                        </button>
                        <button
                          onClick={() => setTolerance(10)}
                          className={`px-3 py-1.5 rounded text-xs font-bold border ${
                            tolerance === 10 ? 'bg-slate-400 text-slate-950 border-slate-300' : 'bg-muted text-foreground border-border'
                          }`}
                        >
                          Silver (±10%)
                        </button>
                        <button
                          onClick={() => setTolerance(20)}
                          className={`px-3 py-1.5 rounded text-xs font-bold border ${
                            tolerance === 20 ? 'bg-slate-600 text-white border-slate-500' : 'bg-muted text-foreground border-border'
                          }`}
                        >
                          No Band (±20%)
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* TOOL 2: Logic Gate Simulator */}
            {activeTool === 'logic' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="border-sky-500/30 bg-card shadow-sm">
                  <CardHeader>
                    <Badge variant="secondary">Digital Electronics &amp; Logic</Badge>
                    <CardTitle className="text-xl">Interactive Logic Gate &amp; Truth Table Simulator</CardTitle>
                    <CardDescription>
                      Toggle inputs X and Y to observe gate conduction states and verify truth tables from Chapter 3 &amp; 4.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Gate selection pills */}
                    <div className="flex flex-wrap gap-1.5">
                      {(['NOT', 'AND', 'OR', 'NAND', 'NOR', 'XOR', 'COMPOUND'] as const).map((g) => (
                        <button
                          key={g}
                          onClick={() => setSelectedGate(g)}
                          className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                            selectedGate === g
                              ? 'bg-sky-500 text-slate-950 font-bold'
                              : 'bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {g === 'COMPOUND' ? 'Compound (Fig 4-24)' : g}
                        </button>
                      ))}
                    </div>

                    {/* Interactive Input Switches */}
                    <div className="flex items-center justify-around p-4 rounded-xl bg-muted/30 dark:bg-slate-950 border border-border">
                      <div className="text-center space-y-2">
                        <span className="text-xs font-bold text-foreground">Input X</span>
                        <div>
                          <button
                            onClick={() => setLogicInputX((prev) => (prev === 1 ? 0 : 1))}
                            className={`px-4 py-2 rounded-lg font-mono font-bold text-base transition-all ${
                              logicInputX === 1
                                ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/30 ring-2 ring-emerald-300'
                                : 'bg-card text-muted-foreground border border-border hover:border-slate-400'
                            }`}
                          >
                            {logicInputX === 1 ? 'HIGH (1)' : 'LOW (0)'}
                          </button>
                        </div>
                      </div>

                      {selectedGate !== 'NOT' && (
                        <div className="text-center space-y-2">
                          <span className="text-xs font-bold text-foreground">Input Y</span>
                          <div>
                            <button
                              onClick={() => setLogicInputY((prev) => (prev === 1 ? 0 : 1))}
                              className={`px-4 py-2 rounded-lg font-mono font-bold text-base transition-all ${
                                logicInputY === 1
                                ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/30 ring-2 ring-emerald-300'
                                : 'bg-card text-muted-foreground border border-border hover:border-slate-400'
                              }`}
                            >
                              {logicInputY === 1 ? 'HIGH (1)' : 'LOW (0)'}
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="text-center space-y-2">
                        <span className="text-xs font-bold text-amber-600 dark:text-amber-400">Output Result</span>
                        <div
                          className={`px-5 py-2 rounded-lg font-mono font-black text-lg transition-all ${
                            logicOutput === 1
                              ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/40 animate-pulse'
                              : 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/80'
                          }`}
                        >
                          {logicOutput === 1 ? 'TRUE (1)' : 'FALSE (0)'}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl border border-border bg-muted/20 dark:bg-slate-950 text-xs space-y-2">
                      <div className="font-bold text-sky-600 dark:text-sky-400 flex items-center gap-1.5">
                        <Info className="h-4 w-4" />
                        Gate Logic Rationale:
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {selectedGate === 'AND' && 'An AND gate outputs 1 only when BOTH Input X AND Input Y are 1. Any low input forces the output to 0.'}
                        {selectedGate === 'OR' && 'An inclusive OR gate outputs 1 if Input X OR Input Y (or both) are 1. Output is 0 only when both inputs are 0.'}
                        {selectedGate === 'NOT' && 'A NOT gate (inverter) reverses the input state. 1 becomes 0, and 0 becomes 1.'}
                        {selectedGate === 'NAND' && 'A NAND gate is an AND followed by an inverter. It outputs 0 only when both inputs are 1; otherwise outputs 1.'}
                        {selectedGate === 'NOR' && 'A NOR gate is an OR followed by an inverter. It outputs 1 only when both inputs are 0; otherwise outputs 0.'}
                        {selectedGate === 'XOR' && 'An Exclusive-OR (XOR) gate outputs 1 only when the two inputs differ (0,1 or 1,0). If both are the same, output is 0.'}
                        {selectedGate === 'COMPOUND' && 'Case 1 (Fig 4-24): Both inputs are inverted into an AND gate: (NOT X) AND (NOT Y). By DeMorgan\'s Theorem, this produces an output identically equal to X NOR Y!'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* SVG Truth Table Reference */}
                <Card className="border-border bg-card shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Live Truth Table Verification</CardTitle>
                    <CardDescription>Active row highlighted in real-time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-muted text-foreground uppercase font-mono">
                          <tr>
                            <th className="p-3">Input X</th>
                            {selectedGate !== 'NOT' && <th className="p-3">Input Y</th>}
                            <th className="p-3 text-amber-600 dark:text-amber-400">Output</th>
                            <th className="p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border/60">
                          {[
                            { x: 0, y: 0 },
                            { x: 0, y: 1 },
                            { x: 1, y: 0 },
                            { x: 1, y: 1 },
                          ]
                            .filter((row) => (selectedGate === 'NOT' ? row.y === 0 : true))
                            .map((row, idx) => {
                              const isActive =
                                row.x === logicInputX && (selectedGate === 'NOT' || row.y === logicInputY);
                              let expectedOut = 0;
                              if (selectedGate === 'NOT') expectedOut = row.x === 1 ? 0 : 1;
                              if (selectedGate === 'AND') expectedOut = row.x && row.y ? 1 : 0;
                              if (selectedGate === 'OR') expectedOut = row.x || row.y ? 1 : 0;
                              if (selectedGate === 'NAND') expectedOut = row.x && row.y ? 0 : 1;
                              if (selectedGate === 'NOR') expectedOut = !row.x && !row.y ? 1 : 0;
                              if (selectedGate === 'XOR') expectedOut = row.x !== row.y ? 1 : 0;
                              if (selectedGate === 'COMPOUND') expectedOut = !row.x && !row.y ? 1 : 0;

                              return (
                                <tr
                                  key={idx}
                                  className={`transition-colors font-mono ${
                                    isActive
                                      ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300 font-bold'
                                      : 'hover:bg-muted/50 text-foreground'
                                  }`}
                                >
                                  <td className="p-3">{row.x}</td>
                                  {selectedGate !== 'NOT' && <td className="p-3">{row.y}</td>}
                                  <td className="p-3 font-bold">{expectedOut}</td>
                                  <td className="p-3">
                                    {isActive ? (
                                      <Badge variant="warning" className="text-[10px]">
                                        CURRENT STATE
                                      </Badge>
                                    ) : (
                                      <span className="text-muted-foreground">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* TOOL 3: Voltage Divider vs Diode Reducer Load Sag */}
            {activeTool === 'regulation' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="border-amber-500/30 bg-card shadow-sm">
                  <CardHeader>
                    <Badge variant="warning">Chapter 6 Laboratory Simulation</Badge>
                    <CardTitle className="text-xl">Resistive Divider Sag vs. Diode Reducer</CardTitle>
                    <CardDescription>
                      Drag the load resistance slider (RL) to observe experimental bench findings comparing load regulation characteristics.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-foreground">
                        <span>External Load Resistance (RL):</span>
                        <span className="font-mono text-amber-600 dark:text-amber-400 text-sm">{loadResistance} Ω</span>
                      </div>
                      <input
                        type="range"
                        min="220"
                        max="3300"
                        step="10"
                        value={loadResistance}
                        onChange={(e) => setLoadResistance(parseInt(e.target.value))}
                        className="w-full accent-amber-500"
                      />
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>220 Ω (Heavy Load)</span>
                        <span>1,000 Ω</span>
                        <span>3,300 Ω (Light Load)</span>
                      </div>
                    </div>

                    {/* Side-by-Side Readouts */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Resistive Divider Output */}
                      <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-center space-y-1">
                        <span className="text-xs font-bold text-rose-700 dark:text-rose-300">Resistive Divider (E2)</span>
                        <div className="text-3xl font-black text-rose-600 dark:text-rose-400 font-mono">
                          {regulationData.vDivider} V
                        </div>
                        <span className="text-[10px] text-rose-600/80 dark:text-rose-300/80">No-load: 2.04 V (Severe Sag!)</span>
                      </div>

                      {/* Diode Reducer Output */}
                      <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-center space-y-1">
                        <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">2-Diode Reducer</span>
                        <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
                          {regulationData.vDiode} V
                        </div>
                        <span className="text-[10px] text-emerald-600/80 dark:text-emerald-300/80">No-load: 5.70 V (Rock Solid)</span>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl border border-border bg-muted/20 dark:bg-slate-950 text-xs space-y-2">
                      <div className="font-bold text-foreground flex items-center gap-1.5">
                        <Info className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                        Engineering Takeaway:
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        Notice how the resistive divider drops from <strong>2.04 V</strong> down to <strong>0.76 V</strong> (a 63% loss) because heavy load current shunts the tap resistance.
                        In contrast, the 2-diode reducer stays between <strong>5.08 V and 4.79 V</strong> across the entire range! Silicon forward breakover (~0.7V) clamps voltage reliably.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* SVG Curves Diagram */}
                <div className="rounded-xl overflow-hidden border border-border bg-slate-950 p-2 flex items-center justify-center">
                  <img
                    src="/diagrams/diag_voltage_divider_vs_diode_reducer.svg"
                    alt="Voltage Divider vs Diode Reducer Curves"
                    className="w-full h-auto max-h-[380px] object-contain"
                  />
                </div>
              </div>
            )}

            {/* TOOL 4: Op-Amp Active Filter Calculator */}
            {activeTool === 'filter' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="border-sky-500/30 bg-card shadow-sm">
                  <CardHeader>
                    <Badge variant="secondary">Active RC Filter Topologies</Badge>
                    <CardTitle className="text-xl">Op-Amp Active Filter Frequency Calculator</CardTitle>
                    <CardDescription>
                      Calculate cutoff frequency fc = 1 / (2πRC) for lowpass and highpass active filters from Chapter 5.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex gap-2">
                      {(['lowpass', 'highpass', 'peak', 'notch'] as const).map((t) => (
                        <button
                          key={t}
                          onClick={() => setFilterType(t)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-all ${
                            filterType === t
                              ? 'bg-sky-500 text-slate-950'
                              : 'bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {t} Filter
                        </button>
                      ))}
                    </div>

                    <div className="space-y-4">
                      {/* Resistor R slider */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs font-bold text-foreground">
                          <span>Resistance R:</span>
                          <span className="font-mono text-sky-600 dark:text-sky-400">{filterResistorKOhms} kΩ ({filterResistorKOhms * 1000} Ω)</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="100"
                          value={filterResistorKOhms}
                          onChange={(e) => setFilterResistorKOhms(parseInt(e.target.value))}
                          className="w-full accent-sky-500"
                        />
                      </div>

                      {/* Capacitor C slider */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs font-bold text-foreground">
                          <span>Capacitance C:</span>
                          <span className="font-mono text-sky-600 dark:text-sky-400">{filterCapacitorMicroF} µF</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="100"
                          value={Math.round(filterCapacitorMicroF * 1000)}
                          onChange={(e) => setFilterCapacitorMicroF(parseInt(e.target.value) / 1000)}
                          className="w-full accent-sky-500"
                        />
                      </div>
                    </div>

                    <div className="p-4 rounded-xl border border-sky-500/30 bg-sky-500/10 text-center space-y-1">
                      <span className="text-xs font-bold text-foreground">Calculated Cutoff / Center Frequency (fc)</span>
                      <div className="text-3xl font-black text-sky-600 dark:text-sky-400 font-mono">
                        {filterCutoffHz >= 1000 ? `${(filterCutoffHz / 1000).toFixed(2)} kHz` : `${filterCutoffHz} Hz`}
                      </div>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        Formula: fc = 1 / (2 × π × {filterResistorKOhms * 1000}Ω × {filterCapacitorMicroF}µF)
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Active Filter Curves Image */}
                <div className="rounded-xl overflow-hidden border border-border bg-slate-950 p-2 flex items-center justify-center">
                  <img
                    src="/diagrams/diag_opamp_active_filters.svg"
                    alt="Active Filter Response Curves"
                    className="w-full h-auto max-h-[380px] object-contain"
                  />
                </div>
              </div>
            )}

            {/* TOOL 5: Multimeter Test Points Simulator */}
            {activeTool === 'testpoints' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="border-amber-500/30 bg-card shadow-sm">
                  <CardHeader>
                    <Badge variant="warning">Chapter 4 &amp; 6 Diagnostic Bench</Badge>
                    <CardTitle className="text-xl">Transistor Amplifier Test Point (TP) Bench</CardTitle>
                    <CardDescription>
                      Inject circuit faults and place your virtual multimeter probe on TP1 (Emitter), TP2 (Base), and TP3 (Collector) to diagnose defects.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Fault Selector */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-foreground">Select Injected Circuit Condition:</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setAmplifierFault('normal')}
                          className={`p-2 rounded-lg text-xs font-bold border text-left transition-all ${
                            amplifierFault === 'normal' ? 'bg-emerald-500/15 border-emerald-500 text-emerald-800 dark:text-emerald-300' : 'bg-card text-muted-foreground hover:text-foreground border-border'
                          }`}
                        >
                          1. Normal Operating Circuit
                        </button>
                        <button
                          onClick={() => setAmplifierFault('open_r1')}
                          className={`p-2 rounded-lg text-xs font-bold border text-left transition-all ${
                            amplifierFault === 'open_r1' ? 'bg-rose-500/15 border-rose-500 text-rose-800 dark:text-rose-300' : 'bg-card text-muted-foreground hover:text-foreground border-border'
                          }`}
                        >
                          2. Open Emitter Resistor (R1)
                        </button>
                        <button
                          onClick={() => setAmplifierFault('open_r4')}
                          className={`p-2 rounded-lg text-xs font-bold border text-left transition-all ${
                            amplifierFault === 'open_r4' ? 'bg-rose-500/15 border-rose-500 text-rose-800 dark:text-rose-300' : 'bg-card text-muted-foreground hover:text-foreground border-border'
                          }`}
                        >
                          3. Open Collector Resistor (R4)
                        </button>
                        <button
                          onClick={() => setAmplifierFault('short_c1')}
                          className={`p-2 rounded-lg text-xs font-bold border text-left transition-all ${
                            amplifierFault === 'short_c1' ? 'bg-rose-500/15 border-rose-500 text-rose-800 dark:text-rose-300' : 'bg-card text-muted-foreground hover:text-foreground border-border'
                          }`}
                        >
                          4. Shorted Emitter Cap (C1)
                        </button>
                      </div>
                    </div>

                    {/* Test Point Probes */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-foreground">Place Red Meter Probe On:</label>
                      <div className="flex flex-wrap gap-2">
                        {(['TP1', 'TP2', 'TP3', 'VCC', 'GND'] as const).map((tp) => (
                          <button
                            key={tp}
                            onClick={() => setSelectedTP(tp)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-all ${
                              selectedTP === tp
                                ? 'bg-amber-500 text-slate-950 ring-2 ring-amber-400 scale-105'
                                : 'bg-card text-foreground border border-border hover:border-slate-400'
                            }`}
                          >
                            {tp === 'TP1' ? 'TP1 (Emitter)' : tp === 'TP2' ? 'TP2 (Base)' : tp === 'TP3' ? 'TP3 (Collector)' : tp}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Digital Multimeter Display */}
                    <div className="p-4 rounded-xl bg-slate-900 dark:bg-slate-950 border-2 border-slate-700 dark:border-slate-800 space-y-2">
                      <div className="flex justify-between items-center text-xs text-slate-400">
                        <span>Digital Multimeter (DC Volts)</span>
                        <span className="text-[10px] text-emerald-400 font-mono">BLACK PROBE: GND</span>
                      </div>
                      <div className="text-4xl font-black font-mono text-center tracking-wider text-amber-400">
                        {tpReading.val}
                      </div>
                      <div className="text-xs text-slate-300 text-center font-medium pt-1">
                        {tpReading.desc}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Circuit Schematic Reference */}
                <div className="rounded-xl overflow-hidden border border-border bg-slate-950 p-2 flex items-center justify-center">
                  <img
                    src="/diagrams/diag_schematic_drawing_types_comparison.svg"
                    alt="Schematic Reference"
                    className="w-full h-auto max-h-[380px] object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ================================================================== */}
        {/* TAB 3: SCHEMATIC SYMBOLS DICTIONARY (Appendix A) */}
        {/* ================================================================== */}
        {activeTab === 'symbols' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <Search className="h-5 w-5 text-emerald-500" />
                  Schematic Symbols Encyclopedia (Appendix A Reference)
                </h3>
                <p className="text-xs text-muted-foreground">
                  Comprehensive listing of electronic symbols, IEEE designations, and schematic functions.
                </p>
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search symbols or codes..."
                  value={symbolSearch}
                  onChange={(e) => setSymbolSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap gap-1.5">
              {['All', 'Passives', 'Semiconductors', 'Tubes', 'Switches & Relays', 'Meters & Sources', 'Cables & Wiring'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSymbolCategory(cat)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                    symbolCategory === cat
                      ? 'bg-emerald-500 text-slate-950 font-bold'
                      : 'bg-card text-muted-foreground hover:text-foreground border border-border hover:bg-muted'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Symbols Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSymbols.map((item, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-xl border border-border bg-card hover:border-emerald-500/40 transition-all space-y-3 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-[10px] font-mono">
                      Code: {item.letterCode}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">
                      {item.category}
                    </span>
                  </div>

                  {/* SVG Glyph Render */}
                  <div className="h-14 bg-slate-900 dark:bg-slate-950 rounded-lg flex items-center justify-center p-2 border border-slate-700/50 dark:border-border/50">
                    <svg
                      width="90"
                      height="40"
                      viewBox="0 0 90 40"
                      dangerouslySetInnerHTML={{ __html: item.svgIcon }}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-bold text-foreground">{item.name}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ================================================================== */}
        {/* TAB 4: FINAL CERTIFICATION EXAM */}
        {/* ================================================================== */}
        {activeTab === 'exam' && (
          <div className="space-y-8 max-w-4xl mx-auto">
            {!examActive && !examSubmitted ? (
              <Card className="border-purple-500/40 bg-card text-center p-8 space-y-6 shadow-sm">
                <div className="mx-auto h-16 w-16 rounded-full bg-purple-500/10 dark:bg-purple-500/20 border-2 border-purple-500 flex items-center justify-center">
                  <Award className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
                    Course Certification Assessment
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
                    Test your comprehensive mastery of schematic symbols, signal tracing, circuit laws, tube and solid-state topologies, and troubleshooting workflows.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 max-w-md mx-auto text-center">
                  <div className="p-3 rounded-lg bg-muted/40 border border-border">
                    <div className="text-lg font-bold text-purple-600 dark:text-purple-400 font-mono">30</div>
                    <div className="text-[10px] text-muted-foreground">Total Questions</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/40 border border-border">
                    <div className="text-lg font-bold text-amber-600 dark:text-amber-400 font-mono">80%</div>
                    <div className="text-[10px] text-muted-foreground">Passing Score</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/40 border border-border">
                    <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400 font-mono">Certificate</div>
                    <div className="text-[10px] text-muted-foreground">Upon Completion</div>
                  </div>
                </div>

                <div className="max-w-xs mx-auto space-y-2">
                  <label className="text-xs text-muted-foreground text-left block">Your Name (for Certificate):</label>
                  <input
                    type="text"
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground text-center focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  />
                </div>

                <Button
                  variant="amber"
                  size="lg"
                  onClick={() => setExamActive(true)}
                  className="font-bold gap-2 px-8 shadow-lg shadow-purple-500/20"
                >
                  <Play className="h-4 w-4 fill-current" />
                  Start 30-Question Certification Exam
                </Button>
              </Card>
            ) : examSubmitted ? (
              /* Exam Result & Certificate */
              <div className="space-y-8">
                <Card className="border-border bg-card p-6 text-center space-y-4 shadow-sm">
                  <Badge variant={examScore >= 24 ? 'success' : 'danger'} className="text-xs">
                    {examScore >= 24 ? 'PASSED — CERTIFICATION EARNED' : 'NOT PASSED — RETAKE RECOMMENDED'}
                  </Badge>
                  <h2 className="text-3xl font-black text-foreground">
                    Score: {examScore} / 30 ({Math.round((examScore / 30) * 100)}%)
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {examScore >= 24
                      ? 'Outstanding work! You have successfully mastered electrical schematic reading, circuit tracing, and component-level diagnostic principles.'
                      : 'You scored below the 80% passing threshold. Review the modules and retake the assessment.'}
                  </p>

                  {/* Certificate of Completion */}
                  {examScore >= 24 && (
                    <div className="p-8 rounded-2xl border-4 border-amber-500/60 bg-gradient-to-br from-amber-50/80 via-background to-amber-100/50 dark:from-slate-900 dark:via-slate-950 dark:to-amber-950/40 text-center space-y-4 shadow-2xl relative my-6 text-foreground">
                      <div className="flex justify-center">
                        <Award className="h-16 w-16 text-amber-500 animate-bounce" />
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs tracking-widest uppercase font-bold text-amber-600 dark:text-amber-400">
                          Certificate of Completion
                        </div>
                        <h3 className="text-2xl sm:text-3xl font-serif font-black text-foreground">
                          Electrical Schematic Reading &amp; Circuit Diagnosis
                        </h3>
                      </div>
                      <p className="text-xs text-muted-foreground italic">This is to certify that</p>
                      <div className="text-xl font-bold text-amber-600 dark:text-amber-300 font-serif border-b border-amber-500/40 pb-2 max-w-sm mx-auto">
                        {candidateName || 'Maintenance Technician'}
                      </div>
                      <p className="text-xs text-muted-foreground max-w-lg mx-auto leading-relaxed">
                        has demonstrated comprehensive proficiency in reading electrical blueprints, identifying standard schematic symbols (IEEE/ANSI), interpreting block diagrams and flowcharts, analyzing multistage circuits, and performing component-level troubleshooting.
                      </p>
                      <div className="flex justify-between items-center text-[11px] text-muted-foreground pt-4 border-t border-border">
                        <span>Curriculum: Industrial Electrical Standard (ANSI/IEEE)</span>
                        <span>Verified Score: {Math.round((examScore / 30) * 100)}%</span>
                        <span>Date: {new Date().toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-center gap-3 pt-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setExamActive(true);
                        setExamSubmitted(false);
                        setExamAnswers({});
                      }}
                      className="gap-1.5"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Retake Exam
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setActiveTab('lessons')}
                    >
                      Back to Lessons
                    </Button>
                  </div>
                </Card>

                {/* Question Review List */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-foreground">Detailed Answer Review</h3>
                  {FINAL_EXAM_QUESTIONS.map((q, idx) => {
                    const userAns = examAnswers[q.id];
                    const isCorrect = userAns === q.correctIndex;

                    return (
                      <div
                        key={q.id}
                        className={`p-4 rounded-xl border space-y-2 text-xs ${
                          isCorrect
                            ? 'border-emerald-500/40 bg-emerald-500/10 dark:bg-emerald-950/20'
                            : 'border-rose-500/40 bg-rose-500/10 dark:bg-rose-950/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold text-foreground">Question {idx + 1}</span>
                          <Badge variant={isCorrect ? 'success' : 'danger'} className="text-[10px]">
                            {isCorrect ? 'CORRECT' : 'INCORRECT'}
                          </Badge>
                        </div>
                        <p className="text-sm font-semibold text-foreground">{q.prompt}</p>
                        <div className="text-muted-foreground">
                          Your Answer:{' '}
                          <span className={isCorrect ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-rose-600 dark:text-rose-400 font-bold'}>
                            {userAns !== undefined ? q.options[userAns] : 'Not Answered'}
                          </span>
                        </div>
                        {!isCorrect && (
                          <div className="text-emerald-600 dark:text-emerald-400 font-bold">
                            Correct Answer: {q.options[q.correctIndex]}
                          </div>
                        )}
                        <p className="text-muted-foreground pt-1 border-t border-border/50">{q.explanation}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Active Exam Screen */
              <div className="space-y-6">
                <div className="flex items-center justify-between bg-card p-4 rounded-xl border border-border shadow-sm">
                  <span className="text-xs font-bold text-foreground font-mono">
                    Answered: {Object.keys(examAnswers).length} / {FINAL_EXAM_QUESTIONS.length}
                  </span>
                  <Button
                    variant="amber"
                    size="sm"
                    onClick={handleFinalExamSubmit}
                    disabled={Object.keys(examAnswers).length === 0}
                    className="font-bold text-xs"
                  >
                    Finish &amp; Grade Exam
                  </Button>
                </div>

                <div className="space-y-6">
                  {FINAL_EXAM_QUESTIONS.map((q, idx) => (
                    <Card key={q.id} className="border-border bg-card shadow-sm">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400">
                            Question {idx + 1} of 30
                          </span>
                        </div>
                        <CardTitle className="text-base text-foreground">{q.prompt}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {q.options.map((opt, oIdx) => {
                          const isSelected = examAnswers[q.id] === oIdx;
                          return (
                            <button
                              key={oIdx}
                              onClick={() => setExamAnswers((prev) => ({ ...prev, [q.id]: oIdx }))}
                              className={`w-full text-left p-3 rounded-lg border text-xs transition-all ${
                                isSelected
                                  ? 'border-amber-500 bg-amber-500/15 text-amber-900 dark:text-amber-300 font-bold ring-1 ring-amber-400'
                                  : 'border-border bg-muted/20 hover:bg-muted/60 text-foreground'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <span className="font-mono font-bold shrink-0">{String.fromCharCode(65 + oIdx)}.</span>
                                <span>{opt}</span>
                              </div>
                            </button>
                          );
                        })}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    variant="amber"
                    size="lg"
                    onClick={handleFinalExamSubmit}
                    className="font-bold px-8 shadow-lg shadow-amber-500/20"
                  >
                    Submit 30 Answers &amp; View Score
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COLOR HELPER UTILITIES
// ============================================================================
function getColorHex(digit: number): string {
  switch (digit) {
    case 0: return '#000000'; // Black
    case 1: return '#78350f'; // Brown
    case 2: return '#dc2626'; // Red
    case 3: return '#ea580c'; // Orange
    case 4: return '#eab308'; // Yellow
    case 5: return '#16a34a'; // Green
    case 6: return '#2563eb'; // Blue
    case 7: return '#9333ea'; // Violet
    case 8: return '#64748b'; // Gray
    case 9: return '#f8fafc'; // White
    default: return '#000000';
  }
}

function getToleranceHex(tol: number): string {
  if (tol === 5) return '#ca8a04'; // Gold
  if (tol === 10) return '#94a3b8'; // Silver
  return '#d97706'; // None (body color)
}

function getFailureRateHex(rate: number): string {
  if (rate === 1) return '#78350f'; // Brown 1%
  if (rate === 0.1) return '#dc2626'; // Red 0.1%
  if (rate === 0.01) return '#ea580c'; // Orange 0.01%
  return '#eab308'; // Yellow 0.001%
}

export default SchematicsCoursePage;
